import type { DisclaimerComponent } from '@rainbow-me/rainbowkit';
import {
  cssStringFromTheme,
  darkTheme,
  RainbowKitAuthenticationProvider,
  RainbowKitProvider,
} from '@rainbow-me/rainbowkit';
import { useTranslations } from 'next-intl';
import { useDisconnect, WagmiProvider } from 'wagmi';

import type { BlockchainNetwork } from '@zealy/utils';
import { useAuthenticatedUser } from '@zealy/queries';
import { BLOCKCHAINS } from '@zealy/utils';

import { PRIVACY_URL, TERMS_AND_CONDITIONS_URL } from '#constants';

import { authenticationAdapter } from './authenticationAdapter';
import { config } from './config';

const customDarkTheme = darkTheme({
  accentColor: 'var(--color-text-brand-primary)',
  fontStack: 'system',
});

export const Disclaimer: DisclaimerComponent = ({ Text, Link }) => {
  const t = useTranslations('settings');
  return (
    <Text>
      {t.rich('disclaimer', {
        ToU: children => <Link href={TERMS_AND_CONDITIONS_URL}>{children}</Link>,
        Privacy: children => <Link href={PRIVACY_URL}>{children}</Link>,
      })}
    </Text>
  );
};

const InternalRainbowProvider = ({
  children,
  props,
  blockchain = 'eth-mainnet',
}: {
  children: React.ReactNode;
  props?: React.ComponentProps<typeof RainbowKitProvider>;
  blockchain?: BlockchainNetwork;
}) => {
  const user = useAuthenticatedUser();
  const { disconnect } = useDisconnect();

  const desiredChainId = BLOCKCHAINS[blockchain].chainId;

  return (
    <RainbowKitAuthenticationProvider
      adapter={authenticationAdapter(desiredChainId, async () => {
        await user.refetch();

        // Disconnect Wagmi so that it doesn't keep a reference to the current account
        // and allows to reopen the connect modal instead of the current account one
        return disconnect();
      })}
      status={'unauthenticated'}
    >
      <RainbowKitProvider
        theme={customDarkTheme}
        appInfo={{
          disclaimer: Disclaimer,
        }}
        initialChain={BLOCKCHAINS[blockchain].chainId}
        {...props}
      >
        <style
          dangerouslySetInnerHTML={{
            __html: `
            :root {
              ${cssStringFromTheme(customDarkTheme)}
            }
            
            [data-rk] {
              pointer-events: auto !important; /* Enable interactions in quest preview modal */
            }
          `,
          }}
        />
        {children}
      </RainbowKitProvider>
    </RainbowKitAuthenticationProvider>
  );
};

export const WalletProvider = ({
  children,
  props,
  blockchain = 'eth-mainnet',
}: {
  children: React.ReactNode;
  props?: React.ComponentProps<typeof RainbowKitProvider>;
  blockchain?: BlockchainNetwork;
}) => {
  return (
    <WagmiProvider config={config} reconnectOnMount={false}>
      <InternalRainbowProvider blockchain={blockchain} props={props}>
        {children}
      </InternalRainbowProvider>
    </WagmiProvider>
  );
};
